// ============================================
// FAMUZA MARKET — App Context (Global State)
// Manages: user auth, cart, notifications
// ============================================

import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabase';

const AppContext = createContext(null);

export function AppProvider({ children }) {

  // ---- USER STATE ----
  const [user, setUser] = useState(null);

  // ---- CART STATE — persisted to localStorage ----
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem('famuza_cart');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  // ---- NOTIFICATIONS ----
  const [notifications, setNotifications] = useState([]);

  // ---- FLASH SALE TIMER ----
  const [flashTimer, setFlashTimer] = useState({ h: '05', m: '59', s: '59' });

  // Countdown timer for flash sales
  useEffect(() => {
    let total = 5 * 3600 + 59 * 60 + 59;
    const interval = setInterval(() => {
      if (total <= 0) { clearInterval(interval); return; }
      total--;
      const h = Math.floor(total / 3600);
      const m = Math.floor((total % 3600) / 60);
      const s = total % 60;
      setFlashTimer({
        h: String(h).padStart(2, '0'),
        m: String(m).padStart(2, '0'),
        s: String(s).padStart(2, '0'),
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // ---- CART ACTIONS ----
  const addToCart = (item) => {
    setCart(prev => {
      const exists = prev.find(i => i.id === item.id);
      const newCart = exists
        ? prev.map(i => i.id === item.id ? { ...i, qty: i.qty + 1 } : i)
        : [...prev, { ...item, qty: 1 }];
      localStorage.setItem('famuza_cart', JSON.stringify(newCart));
      return newCart;
    });
    showNotification(`${item.name} added to basket`, 'success');
  };

  const removeFromCart = (itemId) => {
    setCart(prev => {
      const newCart = prev.filter(i => i.id !== itemId);
      localStorage.setItem('famuza_cart', JSON.stringify(newCart));
      return newCart;
    });
  };

  const updateQty = (itemId, qty) => {
    if (qty < 1) { removeFromCart(itemId); return; }
    setCart(prev => {
      const newCart = prev.map(i => i.id === itemId ? { ...i, qty } : i);
      localStorage.setItem('famuza_cart', JSON.stringify(newCart));
      return newCart;
    });
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem('famuza_cart');
  };

  const cartCount = cart.reduce((sum, i) => sum + i.qty, 0);
  const cartTotal = cart.reduce((sum, i) => sum + (i.price * i.qty), 0);

  // Group cart by seller
  const cartBySeller = cart.reduce((acc, item) => {
    const key = item.sellerId;
    if (!acc[key]) {
      acc[key] = {
        sellerId: item.sellerId,
        sellerName: item.sellerName,
        sellerPhone: item.sellerPhone,
        items: [],
        subtotal: 0,
      };
    }
    acc[key].items.push(item);
    acc[key].subtotal += item.price * item.qty;
    return acc;
  }, {});

  const getCommission = (amount) => {
    if (amount < 500)  return amount * 0.03;
    if (amount < 2000) return amount * 0.05;
    return amount * 0.07;
  };

  // ---- AUTH — synced with Supabase ----
  const login = (userData) => setUser(userData);

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };

  // Sync user state with Supabase session on mount + auth changes
  useEffect(() => {
    // Get current session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    // Listen for auth changes (login, logout, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // ---- NOTIFICATIONS ----
  const showNotification = (message, type = 'info') => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 3500);
  };

  // ---- WHATSAPP HELPERS ----
  const openWhatsApp = (phone, message = '') => {
    const clean = phone.replace(/\D/g, '');
    const number = clean.startsWith('260') ? clean : `260${clean}`;
    const encoded = encodeURIComponent(message);
    window.open(`https://wa.me/${number}?text=${encoded}`, '_blank');
  };

  const whatsAppEnquiry = (item, sellerPhone) => {
    const productRef = item.id ? `FM-${String(item.id).padStart(5,'0')}` : 'FM-MARKET';
    const msg =
      `Hi! I saw your listing on FAMUZA Market and I am interested in:\n\n` +
      `*${item.name}*\n` +
      `Price: K${item.price?.toLocaleString()}\n` +
      `Condition: ${item.condition || 'See listing'}\n` +
      `Ref: ${productRef}\n\n` +
      `Is this still available?`;
    openWhatsApp(sellerPhone, msg);
  };

  const callSeller = (sellerPhone) => {
    window.open(`tel:+${sellerPhone}`, '_self');
  };

  // ---- FORMAT HELPERS ----
  const formatPrice = (amount) =>
    `K${Number(amount).toLocaleString('en-ZM', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

  const formatDate = (date) =>
    new Date(date).toLocaleDateString('en-ZM', { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <AppContext.Provider value={{
      user, login, logout,
      isLoggedIn: !!user,
      isSeller: !!user,
      isAdmin: user?.id === 'f8de3126-ff1a-490f-91e9-3d308883bf54',
      cart, cartCount, cartTotal, cartBySeller,
      addToCart, removeFromCart, updateQty, clearCart,
      getCommission,
      flashTimer,
      notifications, showNotification,
      openWhatsApp, whatsAppEnquiry, callSeller,
      formatPrice, formatDate,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used inside AppProvider');
  return ctx;
};
